import { useEffect } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { patientsApi } from './patientsClient';
import type {
  CreatePatientInput,
  QuickCreatePatientInput,
  UpdatePatientInput,
} from '@/types/patients';

export const PATIENTS_KEY = ['patients'] as const;

/**
 * The registry's default list. Split out as its own constant because opening a
 * patient invalidates exactly this list and nothing else — see `usePatient`.
 */
export const RECENT_PATIENTS_KEY = [...PATIENTS_KEY, 'recent'] as const;

interface PatientSearchParams {
  q: string;
  limit?: number;
  offset?: number;
  /** When false, the query stays idle. Default: true. */
  enabled?: boolean;
}

/**
 * Type-ahead patient search. Stays idle until the trimmed query is at least
 * 2 chars (shorter searches are noise and the server never decrypts extras on
 * search anyway). Uses `keepPreviousData` so paging/typing doesn't flash the
 * empty state.
 */
export function usePatientSearch(params: PatientSearchParams) {
  const { q, limit, offset, enabled = true } = params;
  const trimmed = q.trim();
  return useQuery({
    queryKey: [...PATIENTS_KEY, 'search', { q: trimmed, limit, offset }],
    queryFn: () => patientsApi.search({ q: trimmed, limit, offset }),
    enabled: enabled && trimmed.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useRecentPatients(params: { limit?: number; enabled?: boolean } = {}) {
  const { limit, enabled = true } = params;
  return useQuery({
    queryKey: [...RECENT_PATIENTS_KEY, { limit }],
    queryFn: () => patientsApi.recent({ limit }),
    enabled,
    staleTime: 30_000,
  });
}

export function usePatient(id: string | null | undefined, opts: { enabled?: boolean } = {}) {
  const enabled = (opts.enabled ?? true) && !!id;
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: [...PATIENTS_KEY, 'detail', id],
    queryFn: () => patientsApi.get(id as string),
    enabled,
    staleTime: 15_000,
  });

  // Reading one patient is a WRITE on the server: it appends a `patient.view`
  // row to the PHI read trail, and that trail is what the recents list is
  // derived from. So a settled detail fetch leaves the recents cache stale —
  // which is why a just-approved or just-quick-created patient was missing from
  // the registry until a hard refresh: the create invalidated recents *before*
  // the view that would have put them there.
  const { dataUpdatedAt, isSuccess } = query;
  useEffect(() => {
    if (!isSuccess) return;
    qc.invalidateQueries({ queryKey: RECENT_PATIENTS_KEY });
  }, [qc, isSuccess, dataUpdatedAt]);

  return query;
}

export function useCreatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePatientInput) => patientsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PATIENTS_KEY });
    },
  });
}

export function useQuickCreatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuickCreatePatientInput) => patientsApi.quickCreate(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PATIENTS_KEY });
    },
  });
}

export function useUpdatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdatePatientInput }) =>
      patientsApi.update(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PATIENTS_KEY });
    },
  });
}

export function usePatientPhotoUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      blob,
      contentType,
    }: {
      patientId: string;
      blob: Blob;
      contentType: string;
    }) => patientsApi.uploadPhoto(patientId, blob, contentType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PATIENTS_KEY });
    },
  });
}

export function useRemovePatientPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patientId: string) => patientsApi.removePhoto(patientId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PATIENTS_KEY });
    },
  });
}

export function useDeletePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patientsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PATIENTS_KEY });
    },
  });
}
